import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Camera, AlertCircle, Search, Upload, RefreshCw } from 'lucide-react';

/**
 * QrScannerDialog - Componente para leitura de QR Codes
 * Usa html5-qrcode para melhor suporte iOS PWA.
 * Suporta camara, upload de imagem e entrada manual.
 */
export default function QrScannerDialog({
  open,
  onClose,
  scanning,
  scannerReady,
  cameraPermission,
  qrError,
  isIOS,
  showImageUpload,
  manualQrInput,
  setManualQrInput,
  onScan,
  onError,
  onManualSubmit,
  onImageUpload,
  onRequestCameraAccess
}) {
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [scannerStarted, setScannerStarted] = useState(false);
  const [startError, setStartError] = useState(null);
  const mountedRef = useRef(true);

  const stopScanner = useCallback(async () => {
    if (html5QrRef.current) {
      try {
        const state = html5QrRef.current.getState();
        // state 2 = SCANNING
        if (state === 2) {
          await html5QrRef.current.stop();
        }
      } catch (e) {
        // ignore stop errors
      }
      try {
        html5QrRef.current.clear();
      } catch (e) {
        // ignore clear errors
      }
      html5QrRef.current = null;
    }
    if (mountedRef.current) {
      setScannerStarted(false);
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (!scannerRef.current || html5QrRef.current) return;
    setStartError(null);

    try {
      // Dynamically import to avoid SSR issues
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const elementId = scannerRef.current.id;
      const qrScanner = new Html5Qrcode(elementId, /* verbose */ false);
      html5QrRef.current = qrScanner;

      const config = {
        fps: isIOS ? 5 : 10,
        qrbox: { width: 220, height: 220 },
        aspectRatio: 1.0,
        disableFlip: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };

      await qrScanner.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          // Sucesso: chamar callback no formato esperado pelo OperadorHome
          if (mountedRef.current) {
            onScan([{ rawValue: decodedText }]);
          }
        },
        () => {
          // QR not found in frame - ignore silently
        }
      );

      if (mountedRef.current) {
        setScannerStarted(true);
      }
    } catch (err) {
      console.error('[QR html5] Start error:', err);
      if (mountedRef.current) {
        setStartError(err?.message || String(err));
        onError(err);
      }
    }
  }, [isIOS, onScan, onError]);

  // Start scanner when dialog opens and camera is ready
  useEffect(() => {
    mountedRef.current = true;
    if (open && scanning && scannerReady && scannerRef.current && !html5QrRef.current) {
      // Small delay to ensure DOM is painted
      const timer = setTimeout(() => {
        if (mountedRef.current) startScanner();
      }, 400);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [open, scanning, scannerReady, startScanner]);

  // Clean up on close
  useEffect(() => {
    if (!open) {
      stopScanner();
      setStartError(null);
    }
  }, [open, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, [stopScanner]);

  const handleRetry = async () => {
    await stopScanner();
    setStartError(null);
    onRequestCameraAccess();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-sm rounded-3xl" 
        data-testid="qr-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-heading text-navy flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Ler QR Code
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Aponte a camara para o QR Code da OF
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scanner video area */}
          <div className="relative bg-stone-900 rounded-2xl overflow-hidden aspect-square">
            {/* html5-qrcode mounts video inside this div */}
            <div 
              id="qr-reader" 
              ref={scannerRef} 
              className="w-full h-full"
              style={{ minHeight: 280 }}
            />

            {/* Overlay: waiting for camera */}
            {open && !scanning && !qrError && !startError && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-900">
                <div className="text-center">
                  <Camera className="h-16 w-16 text-stone-600 mx-auto mb-2 animate-pulse" />
                  <p className="text-stone-400 text-sm">A solicitar acesso a camara...</p>
                  <p className="text-stone-500 text-xs mt-2">Por favor, permita o acesso quando solicitado</p>
                </div>
              </div>
            )}

            {/* Overlay: permission denied */}
            {cameraPermission === 'denied' && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-900">
                <div className="text-center p-4">
                  <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-2" />
                  <p className="text-stone-300 text-sm font-medium">Camara nao disponivel</p>
                  <p className="text-stone-500 text-xs mt-2">
                    {isIOS 
                      ? 'Va a Definicoes > Safari > Camara e permita o acesso'
                      : 'Verifique as permissoes do browser'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 text-xs"
                    onClick={handleRetry}
                    data-testid="qr-retry-camera"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Tentar novamente
                  </Button>
                </div>
              </div>
            )}

            {/* Overlay: html5-qrcode start error */}
            {startError && cameraPermission !== 'denied' && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-900">
                <div className="text-center p-4">
                  <AlertCircle className="h-12 w-12 text-amber-400 mx-auto mb-2" />
                  <p className="text-stone-300 text-sm font-medium">Erro ao iniciar camara</p>
                  <p className="text-stone-500 text-xs mt-1 max-w-[250px]">{startError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 text-xs"
                    onClick={handleRetry}
                    data-testid="qr-retry-start"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Tentar novamente
                  </Button>
                </div>
              </div>
            )}

            {/* Scanning guide overlay */}
            {scannerStarted && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 relative">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white/80 rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white/80 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white/80 rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white/80 rounded-br-lg" />
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {qrError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-sm">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p>{qrError}</p>
                {isIOS && cameraPermission === 'denied' && (
                  <p className="text-xs mt-1 text-red-600">
                    <strong>iOS:</strong> Definicoes - Safari - Camara - Permitir
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Image upload fallback */}
          {showImageUpload && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs text-blue-700 mb-2 font-medium">
                Alternativa: Envie uma foto do QR Code
              </p>
              <label className="flex items-center justify-center gap-2 p-3 bg-white border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                <Upload className="h-5 w-5 text-blue-500" />
                <span className="text-sm text-blue-600">Selecionar imagem</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={onImageUpload}
                  data-testid="qr-image-upload"
                />
              </label>
            </div>
          )}

          {/* Manual input */}
          <div className={cameraPermission === 'denied' ? 'bg-amber-50 p-3 rounded-xl border border-amber-200' : ''}>
            <p className="text-xs text-stone-500 mb-2">
              {cameraPermission === 'denied' 
                ? 'Use a entrada manual:' 
                : 'Ou digite o numero da OF manualmente:'}
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: OF-2024-001 ou link do lote"
                className="flex-1 rounded-full"
                value={manualQrInput}
                onChange={(e) => setManualQrInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    onManualSubmit();
                  }
                }}
                autoFocus={false}
                autoComplete="off"
                inputMode="text"
                data-testid="manual-qr-input"
              />
              <Button
                className="rounded-full"
                onClick={onManualSubmit}
                data-testid="manual-qr-submit"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full rounded-full"
            onClick={onClose}
            data-testid="qr-cancel-btn"
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
